import { ReactNode, StrictMode, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import theme from './theme';
import './index.css';
import { Web3Provider } from './components/Web3Provider.tsx';
import { RewardData, YieldData } from './context/context.ts';
import { UserBalances } from './context/context.ts';
import { AppContext } from './context/context.ts';
import { Vault } from './types/vaults.ts';
import axios from 'axios';
import { useAccount } from 'wagmi';
import { RouterProvider } from 'react-router-dom';
import router from './Routes.tsx';
import { L1Conf, currentInstances } from './conf/liquidInstancesConf.ts';
import { LiquidInstanceContext } from './context/liquidInstanceContext.ts';
import { Instance, LiquidInstanceConfiguration } from './types/liquidInstance.ts';
import { MetricsData } from './types/yieldData.ts';

interface AppContextProviderProps {
  children: ReactNode;
}

interface LiquidInstanceContextProviderProps {
  children: ReactNode;
}

export const AppContextProvider = ({ children }: AppContextProviderProps) => {
  const apiUrl = import.meta.env.VITE_BACKEND_API;
  const rUSDCApi = import.meta.env.VITE_RUSDC_API;
  const { address, isConnected } = useAccount();
  const [vaults, setVaults] = useState<Vault[]>([] as Vault[]);
  const [rewardData, setRewardData] = useState<RewardData>({} as RewardData);
  const [yieldData, setYieldData] = useState<YieldData>({} as YieldData);
  const [userBalances, setUserBalances] = useState<UserBalances>({
    global: 0
  });
  const [instancesLiveData, setInstancesLiveData] = useState<MetricsData>({} as MetricsData);

  const value = {
    vaults,
    rewardData,
    userBalances,
    instancesLiveData,
    yieldData
  };

  useEffect(() => {
    const fetchYieldData = async () => {
      try {
        const { data } = await axios.get(`${rUSDCApi}/yield`);
        setYieldData(data);
      } catch (error) {
        console.error('Failed to fetch yield data:', error);
      }
    };

    const fetchInstancesLiveData = async () => {
      try {
        const { data } = await axios.get(`${rUSDCApi}/live-metrics`);
        setInstancesLiveData(data.metrics);
      } catch (error) {
        console.error('Failed to fetch instances live data:', error);
      }
    };
    const fetchVaults = async () => {
      try {
        const { data } = await axios.get(`${apiUrl}/vaults`);
        setVaults(data.vaults);
      } catch (error) {
        console.error('Failed to fetch vaults:', error);
      }
    };
    const fetchRewardData = async () => {
      try {
        const { data } = await axios.get(`${apiUrl}/allrewards`);
        const rewardsObject: RewardData = {};
        Object.entries(data).forEach(([key, value]) => {
          rewardsObject[key] = value as string;
        });
        setRewardData(rewardsObject);
      } catch (error) {
        console.error('Failed to fetch reward data:', error);
      }
    };
    const fetchUserBalances = async () => {
      try {
        const { data } = await axios.get(`${apiUrl}/deposits/${address}`);
        setUserBalances(data);
      } catch (error) {
        console.error('Failed to fetch user deposits:', error);
      }
    };
    fetchInstancesLiveData();
    fetchYieldData();
    fetchVaults();
    fetchRewardData();
    if (isConnected) {
      fetchUserBalances();
    }
  }, [address, apiUrl, isConnected]);

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const LiquidInstanceContextProvider = ({ children }: LiquidInstanceContextProviderProps) => {
  const [instances, setInstances] = useState<Instance[]>([]);
  const [liquidInstanceConfiguration, setLiquidInstanceConfiguration] = useState<LiquidInstanceConfiguration>(
    {} as LiquidInstanceConfiguration
  );
  const [wrap, setWrap] = useState(true);
  const [fromToken, setFromToken] = useState('');
  const [toToken, setToToken] = useState('');
  const [normalizedAmount, setNormalizedAmount] = useState(BigInt(0));
  const liquidInstanceContext = {
    instances,
    liquidInstanceConfiguration,
    setLiquidInstanceConfiguration,
    wrap,
    setWrap,
    fromToken,
    setFromToken,
    toToken,
    setToToken,
    normalizedAmount,
    setNormalizedAmount
  };

  useEffect(() => {
    const instances = currentInstances;
    setInstances(instances);
    setLiquidInstanceConfiguration({
      L1: L1Conf,
      L2: instances[0]
    });
    setToToken(instances[0].L2TokenName);
  }, []);

  return <LiquidInstanceContext.Provider value={liquidInstanceContext}>{children}</LiquidInstanceContext.Provider>;
};

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Web3Provider>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AppContextProvider>
          <LiquidInstanceContextProvider>
            <RouterProvider router={router} />
          </LiquidInstanceContextProvider>
        </AppContextProvider>
      </ThemeProvider>
    </Web3Provider>
  </StrictMode>
);
